import { v4 as uuidv4 } from 'uuid';
import {
  ActionTaken,
  AIChannelType,
  DelayEventWhere,
  DelayNoteAddedBy,
  DelayNotifyChannel,
  DelayStatusMilestone,
  IntervalUnit,
  type DelayAlertActionTakenEvent,
  type DelayAlertFilter,
  type DelayDuration,
  type DelayIfNothingHappens,
  type DelayUntilEvent,
  type DelayWaitEvent,
} from '@/proto/logiblocks/logiblocks';
import { ALERT_TYPE_LABELS } from '@/common/alertType';
import type { ValidationError } from '@/pages/Logiblocks/fieldValidation';
import { MSG_REQUIRED } from '@/pages/Logiblocks/validationMessages';
import type {
  GraphData,
  ReactFlowEdge,
  ReactFlowNode,
} from '@/pages/Logiblocks/types';
import { getAncestorNodes } from '@/pages/Logiblocks/graphTraversal';
import { secondsPerUnit } from '@/pages/Logiblocks/intervalUnits';
import { DelayUntilEventFields } from './delayUntilEventFields';

export { DelayUntilEventFields };

/** Validation field name of one event card. Keyed by the card's id so the card can show its own error. */
export const delayEventField = (eventId: string): string => `Event ${eventId}`;

// The backend checks the same limits at publish: keep MIN_WAIT_SECONDS and MAX_WAIT_SECONDS in
// backend/logiblocks/graph/delay_events.py in step with these.
export const MIN_STOP_WAITING_SECONDS = 5 * 60;
export const MAX_STOP_WAITING_SECONDS = 30 * 24 * 3600;
const MSG_DEADLINE_RANGE =
  'Enter a whole number of minutes or hours, from 5 min to 720 hr';
const MSG_NEEDS_ALERT = 'Choose at least one alert type';
const MSG_NEEDS_ACTION = 'Choose which action to wait for';
const MSG_NEEDS_STATUS = 'Choose at least one status';
const MSG_NEEDS_WHERE = 'Choose where.';
// Same wording as the publish check (delay_until_event_requires_alert_trigger in
// backend/logiblocks/graph/delay_action_node.py).
const needsAlertTriggerMessage = (eventName: string) =>
  `${eventName} can only be used in a workflow that starts from an alert.`;
/** Shown under "Stop waiting after". */
export const MSG_DEADLINE_HELP = 'From 5 min to 720 hr (30 days).';

/** 72 hours: what "Stop waiting after" starts at when the mode is first chosen. */
export const DEFAULT_STOP_WAITING_AFTER: DelayDuration = {
  seconds: 72 * 3600,
  display_unit: IntervalUnit.HOURS,
};

/** "End the run" starts ticked on a new node (PRD 5.1, 5.6). A saved node keeps what it saved. */
export const createDefaultIfNothingHappens = (): DelayIfNothingHappens => ({
  end_run: true,
  notify_automation_owner: false,
  notify_others: undefined,
});

export const createDefaultUntilEvent = (): DelayUntilEvent => ({
  events: [],
  stop_waiting_after: { ...DEFAULT_STOP_WAITING_AFTER },
  if_nothing_happens: createDefaultIfNothingHappens(),
});

// ---------------------------------------------------------------------------
// Event kinds
// ---------------------------------------------------------------------------

export enum DelayEventKind {
  ALERT_TRIGGERED = 'alert_triggered',
  ALERT_ACTION_TAKEN = 'alert_action_taken',
  SNOOZE_ENDED = 'snooze_ended',
  STATUS_PROGRESS_UPDATE = 'status_progress_update',
  REPLY_RECEIVED = 'reply_received',
  DOCUMENT_ADDED = 'document_added',
  NOTE_ADDED = 'note_added',
}

interface EventKindMeta {
  label: string;
  /** Group heading in the "Add event" menu. */
  menuGroup: string;
  /** Text after "Continue when:" on the event's screen. */
  continueWhen: string;
  /** The grey line under the name on the "Add event" screen. */
  description: string;
}

export const EVENT_KIND_META: Record<DelayEventKind, EventKindMeta> = {
  [DelayEventKind.ALERT_TRIGGERED]: {
    label: 'Alert triggered',
    menuGroup: 'Alerts',
    continueWhen: 'a selected alert is triggered on this shipment.',
    description: 'A selected alert is raised on this shipment',
  },
  [DelayEventKind.ALERT_ACTION_TAKEN]: {
    label: 'Alert action taken',
    menuGroup: 'Alerts',
    continueWhen:
      'a selected alert is acted on by a person or an AI agent, or assigned',
    description:
      'A selected alert is acted on by a person or an AI agent, or assigned',
  },
  [DelayEventKind.SNOOZE_ENDED]: {
    label: 'Snooze ended',
    menuGroup: 'Alerts',
    continueWhen:
      'the alert that started this run is no longer snoozed. If it is not snoozed when this Delay starts, the run continues at once.',
    description: "This run's alert is no longer snoozed",
  },
  [DelayEventKind.STATUS_PROGRESS_UPDATE]: {
    label: 'Status progress update',
    menuGroup: 'Shipment status',
    continueWhen: 'the shipment status updates',
    description: 'The shipment reaches a selected status',
  },
  [DelayEventKind.REPLY_RECEIVED]: {
    label: 'Reply received',
    menuGroup: 'Reply received',
    continueWhen:
      'someone replies to a communication sent earlier in this run.',
    description: 'Someone replies to communication sent earlier',
  },
  [DelayEventKind.DOCUMENT_ADDED]: {
    label: 'Document added',
    menuGroup: 'Chain of custody',
    continueWhen: 'a file is attached to the shipment or one of its alerts.',
    description: 'A file is attached to the shipment or one of its alerts',
  },
  [DelayEventKind.NOTE_ADDED]: {
    label: 'Note added',
    menuGroup: 'Chain of custody',
    continueWhen: 'a note is added to the shipment or one of its alerts.',
    description: 'A note is added to the shipment or one of its alerts',
  },
};

/** The kinds the first version offers, in menu order. */
export const OFFERED_EVENT_KINDS: DelayEventKind[] = [
  DelayEventKind.ALERT_TRIGGERED,
  DelayEventKind.ALERT_ACTION_TAKEN,
  DelayEventKind.SNOOZE_ENDED,
  DelayEventKind.STATUS_PROGRESS_UPDATE,
  DelayEventKind.REPLY_RECEIVED,
  DelayEventKind.DOCUMENT_ADDED,
  DelayEventKind.NOTE_ADDED,
];

/** The kind of a saved event, or undefined for an event kind this editor does not know. */
export function getEventKind(
  event: DelayWaitEvent
): DelayEventKind | undefined {
  return OFFERED_EVENT_KINDS.find((kind) => event[kind] !== undefined);
}

// uuid, not crypto.randomUUID, which only exists on https and localhost.
export const newEventId = (): string => uuidv4();

const EMPTY_ALERT_FILTER: DelayAlertFilter = {
  alert_types: [],
  select_all_alert_types: false,
};

/** A new event of the given kind with its starting settings. */
export function createEvent(kind: DelayEventKind, id: string): DelayWaitEvent {
  switch (kind) {
    case DelayEventKind.ALERT_TRIGGERED:
      return { id, alert_triggered: { alerts: { ...EMPTY_ALERT_FILTER } } };
    case DelayEventKind.ALERT_ACTION_TAKEN:
      return {
        id,
        alert_action_taken: {
          alerts: { ...EMPTY_ALERT_FILTER },
          action_taken: [],
          select_all_action_taken: true,
        },
      };
    case DelayEventKind.SNOOZE_ENDED:
      return { id, snooze_ended: {} };
    case DelayEventKind.STATUS_PROGRESS_UPDATE:
      return { id, status_progress_update: { statuses: [] } };
    case DelayEventKind.REPLY_RECEIVED:
      return { id, reply_received: { source_node_ids: [] } };
    case DelayEventKind.DOCUMENT_ADDED:
      return {
        id,
        document_added: {
          where: DelayEventWhere.DELAY_EVENT_WHERE_UNSPECIFIED,
        },
      };
    case DelayEventKind.NOTE_ADDED:
      return {
        id,
        note_added: {
          added_by: DelayNoteAddedBy.DELAY_NOTE_ADDED_BY_ANYONE,
          where: DelayEventWhere.DELAY_EVENT_WHERE_UNSPECIFIED,
        },
      };
  }
}

// ---------------------------------------------------------------------------
// Alert filter
// ---------------------------------------------------------------------------

/** Every alert type is picked when the flag is on; alert_types is then empty. */
export const isAlertTypePicked = (
  filter: DelayAlertFilter | undefined,
  alertType: string
): boolean =>
  !!filter &&
  (filter.select_all_alert_types || filter.alert_types.includes(alertType));

const hasAlertSelection = (filter: DelayAlertFilter | undefined): boolean =>
  !!filter && (filter.select_all_alert_types || filter.alert_types.length > 0);

/**
 * Ticks or unticks `changed` alert types. `allTypes` is every type the picker
 * offers: ticking them all stores "select all" with no list, as the contract
 * says, and unticking one while "select all" is on spells the rest out.
 */
export function setAlertTypesPicked(
  filter: DelayAlertFilter | undefined,
  allTypes: string[],
  changed: string[],
  picked: boolean
): DelayAlertFilter {
  const current = new Set(
    filter?.select_all_alert_types ? allTypes : (filter?.alert_types ?? [])
  );
  for (const alertType of changed) {
    if (picked) current.add(alertType);
    else current.delete(alertType);
  }
  const everythingPicked =
    allTypes.length > 0 &&
    current.size === allTypes.length &&
    allTypes.every((t) => current.has(t));
  if (everythingPicked) {
    return { alert_types: [], select_all_alert_types: true };
  }
  return { alert_types: [...current], select_all_alert_types: false };
}

// ---------------------------------------------------------------------------
// Alert action taken: the "Action" radio
// ---------------------------------------------------------------------------

export enum ActionChoice {
  ANY = 'any',
  RESOLVED_BY_PERSON = 'resolved_by_person',
  RESOLVED_BY_AI = 'resolved_by_ai',
  DISMISSED = 'dismissed',
  ASSIGNED = 'assigned',
}

export const ACTION_CHOICE_OPTIONS: { value: ActionChoice; label: string }[] = [
  { value: ActionChoice.ANY, label: 'Any action' },
  { value: ActionChoice.RESOLVED_BY_PERSON, label: 'Resolved by a person' },
  { value: ActionChoice.RESOLVED_BY_AI, label: 'Resolved by an AI agent' },
  { value: ActionChoice.DISMISSED, label: 'Dismissed' },
  { value: ActionChoice.ASSIGNED, label: 'Assigned' },
];

const ACTIONS_OF_CHOICE: Record<
  Exclude<ActionChoice, ActionChoice.ANY>,
  ActionTaken[]
> = {
  [ActionChoice.RESOLVED_BY_PERSON]: [ActionTaken.ACTION_TAKEN_MANUAL_RESOLVED],
  [ActionChoice.RESOLVED_BY_AI]: [ActionTaken.ACTION_TAKEN_AI_RESOLVED],
  [ActionChoice.DISMISSED]: [
    ActionTaken.ACTION_TAKEN_MANUAL_DISMISSED,
    ActionTaken.ACTION_TAKEN_AI_DISMISSED,
    ActionTaken.ACTION_TAKEN_SYSTEM_DISMISSED,
  ],
  [ActionChoice.ASSIGNED]: [
    ActionTaken.ACTION_TAKEN_MANUAL_ASSIGNED,
    ActionTaken.ACTION_TAKEN_SYSTEM_ASSIGNED,
  ],
};

/** The settings an "Action" radio choice stores. */
export function actionSettingsOfChoice(
  choice: ActionChoice
): Pick<
  DelayAlertActionTakenEvent,
  'action_taken' | 'select_all_action_taken'
> {
  if (choice === ActionChoice.ANY) {
    return { action_taken: [], select_all_action_taken: true };
  }
  return {
    action_taken: [...ACTIONS_OF_CHOICE[choice]],
    select_all_action_taken: false,
  };
}

const sameMembers = (a: ActionTaken[], b: ActionTaken[]): boolean =>
  a.length === b.length && b.every((value) => a.includes(value));

/**
 * The radio choice saved settings stand for. Undefined when they match none of
 * the five (for example a list written by the AI builder): the editor shows no
 * radio ticked and leaves the saved list alone until the user picks one.
 */
export function actionChoiceOf(
  event: DelayAlertActionTakenEvent
): ActionChoice | undefined {
  if (event.select_all_action_taken) return ActionChoice.ANY;
  return (
    Object.values(ActionChoice).find(
      (choice) =>
        choice !== ActionChoice.ANY &&
        sameMembers(event.action_taken, ACTIONS_OF_CHOICE[choice])
    ) ?? undefined
  );
}

// ---------------------------------------------------------------------------
// Status progress update
// ---------------------------------------------------------------------------

interface StatusOption {
  value: DelayStatusMilestone;
  label: string;
  /** Used in card summaries. */
  shortLabel: string;
}

/**
 * The statuses offered: the ones something reports. Keep in step with STATUS_PROGRESS_MILESTONES
 * in backend/logiblocks/graph/delay_events.py, which publishing enforces. The contract also has
 * Created, Customs hold, Customs released, Incomplete and Geo-fence route deviation; a card
 * waiting for one of those could only ever time out.
 */
export const STATUS_OPTIONS: StatusOption[] = [
  {
    value: DelayStatusMilestone.DELAY_STATUS_PICKED_UP,
    label: 'In transit (picked up)',
    shortLabel: 'In transit',
  },
  {
    value: DelayStatusMilestone.DELAY_STATUS_DELIVERED,
    label: 'Delivered',
    shortLabel: 'Delivered',
  },
];

/** What a card saved with a status that is no longer offered still calls it. */
const NOT_OFFERED_STATUS_LABELS: Partial<Record<DelayStatusMilestone, string>> =
  {
    [DelayStatusMilestone.DELAY_STATUS_CREATED]: 'Created',
    [DelayStatusMilestone.DELAY_STATUS_CUSTOMS_HOLD]: 'Customs hold',
    [DelayStatusMilestone.DELAY_STATUS_CUSTOMS_RELEASED]: 'Customs released',
    [DelayStatusMilestone.DELAY_STATUS_INCOMPLETE]: 'Incomplete',
    [DelayStatusMilestone.DELAY_STATUS_GEOFENCE_DEVIATION]: 'Route deviation',
  };

/** Shown on the status screen, greyed out and never selectable: it happened before the wait began. */
export const CREATED_STATUS_OPTION = {
  value: DelayStatusMilestone.DELAY_STATUS_CREATED,
  label: 'Created',
};

const statusShortLabel = (status: DelayStatusMilestone): string =>
  STATUS_OPTIONS.find((o) => o.value === status)?.shortLabel ??
  NOT_OFFERED_STATUS_LABELS[status] ??
  'Unknown status';

/** Whether a saved status is one the editor offers. */
export const isOfferedStatus = (status: DelayStatusMilestone): boolean =>
  STATUS_OPTIONS.some((o) => o.value === status);

// ---------------------------------------------------------------------------
// Reply received: which earlier nodes can be replied to
// ---------------------------------------------------------------------------

export interface CommunicationSource {
  nodeId: string;
  /** The channel, for example "SMS". */
  channel: string;
}

/** The channel a node sends on, or undefined when the node sends no message a person could reply to. */
function communicationChannelOf(node: ReactFlowNode): string | undefined {
  const data = node.data;
  if (data.email_action) return 'Email';
  if (data.sms_action) return 'SMS';
  if (data.slack_action) return 'Slack';
  if (data.teams_notification) return 'Microsoft Teams';
  if (
    data.ai_call_action &&
    data.ai_call_action.channel_type !== AIChannelType.CALL
  ) {
    return 'AI agent';
  }
  return undefined;
}

/** Nodes upstream of `nodeId` that send an email, SMS, Slack, Teams or AI-agent message, nearest first. */
export function getEarlierCommunicationNodes(
  nodeId: string,
  nodes: ReactFlowNode[],
  edges: ReactFlowEdge[]
): CommunicationSource[] {
  return getAncestorNodes(nodeId, nodes, edges).flatMap((node) => {
    const channel = communicationChannelOf(node);
    return channel ? [{ nodeId: node.id, channel }] : [];
  });
}

// ---------------------------------------------------------------------------
// Note added: who wrote the note
// ---------------------------------------------------------------------------

export const NOTE_ADDED_BY_OPTIONS: {
  value: DelayNoteAddedBy;
  label: string;
  /** Used in card summaries: "added by {by}". */
  by: string;
}[] = [
  {
    value: DelayNoteAddedBy.DELAY_NOTE_ADDED_BY_ANYONE,
    label: 'Anyone',
    by: 'anyone',
  },
  {
    value: DelayNoteAddedBy.DELAY_NOTE_ADDED_BY_PERSON,
    label: 'A person',
    by: 'a person',
  },
  {
    value: DelayNoteAddedBy.DELAY_NOTE_ADDED_BY_AUTOMATION,
    label: 'An automation',
    by: 'an automation',
  },
];

// ---------------------------------------------------------------------------
// Document added and Note added: where
// ---------------------------------------------------------------------------

export const WHERE_SHIPMENT_LABEL = 'Anywhere on the shipment chain of custody';
/** What "the alert that started the run" is called when the trigger names no single alert type. */
export const WHERE_RUN_ALERT_PLAIN_LABEL =
  'Appended to the alert that started the run';
// Same wording as the publish check (delay_until_event_requires_alert_trigger in
// backend/logiblocks/graph/delay_action_node.py).
const MSG_WHERE_NEEDS_ALERT_TRIGGER =
  'Appended to the alert can only be used in a workflow that starts from an alert.';

/** Whether the workflow's own trigger is an alert, and the alert type it names when it names one. */
export function runAlertOf(nodes: ReactFlowNode[]): {
  startsFromAlert: boolean;
  /** The alert type's name when the trigger is for exactly one, else undefined. */
  alertName: string | undefined;
} {
  // Mirrors the publish check: any alert trigger among the workflow's own (not subgraph) nodes.
  const trigger = nodes.find(
    (node) =>
      !node.parentId &&
      (node.data?.alert_trigger !== undefined ||
        node.data?.alert_action_taken_trigger !== undefined)
  );
  if (!trigger) return { startsFromAlert: false, alertName: undefined };
  const types =
    trigger.data.alert_trigger?.alert_types ??
    trigger.data.alert_action_taken_trigger?.alert_types ??
    [];
  const alertName =
    types.length === 1
      ? (ALERT_TYPE_LABELS as Record<string, string>)[types[0]]
      : undefined;
  return { startsFromAlert: true, alertName };
}

/** The summary of a card's choice, for the card on the Delay. A card with none says so. */
export function whereSummary(where: DelayEventWhere | undefined): string {
  switch (where) {
    case DelayEventWhere.DELAY_EVENT_WHERE_SHIPMENT:
      return 'Anywhere on the chain of custody';
    case DelayEventWhere.DELAY_EVENT_WHERE_RUN_ALERT:
      return 'Appended to the alert';
    case DelayEventWhere.DELAY_EVENT_WHERE_UNSPECIFIED:
    case undefined:
      return 'no place chosen';
    case DelayEventWhere.UNRECOGNIZED:
    default:
      return 'unknown place';
  }
}

// ---------------------------------------------------------------------------
// If nothing happens
// ---------------------------------------------------------------------------

export const NOTIFY_CHANNEL_OPTIONS: {
  value: DelayNotifyChannel;
  label: string;
}[] = [
  { value: DelayNotifyChannel.DELAY_NOTIFY_EMAIL, label: 'Email' },
  { value: DelayNotifyChannel.DELAY_NOTIFY_SMS, label: 'SMS' },
  { value: DelayNotifyChannel.DELAY_NOTIFY_SLACK, label: 'Slack' },
  { value: DelayNotifyChannel.DELAY_NOTIFY_TEAMS, label: 'Teams' },
];

// ---------------------------------------------------------------------------
// Text for the cards, delete dialog and canvas subtitle
// ---------------------------------------------------------------------------

const plural = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

/** An alert type as words: the editor's name for it, or its stored key read as plain words. */
export function alertTypeName(key: string): string {
  const label = (ALERT_TYPE_LABELS as Record<string, string>)[key];
  if (label) return label;
  const words = key.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function alertFilterText(filter: DelayAlertFilter | undefined): string {
  if (filter?.select_all_alert_types) return 'all alert types';
  const types = filter?.alert_types ?? [];
  if (types.length === 0) return 'no alert type chosen';
  if (types.length === 1) return alertTypeName(types[0]);
  return plural(types.length, 'alert type');
}

const ACTION_CHOICE_SUMMARY: Record<ActionChoice, string> = {
  [ActionChoice.ANY]: 'Any action',
  [ActionChoice.RESOLVED_BY_PERSON]: 'Resolved by a person',
  [ActionChoice.RESOLVED_BY_AI]: 'Resolved by an AI agent',
  [ActionChoice.DISMISSED]: 'Dismissed',
  [ActionChoice.ASSIGNED]: 'Assigned',
};

/**
 * The part of a card's summary after the event name, for example "3 alert
 * types". For a reply event pass the channels of the chosen nodes; an empty
 * list means any reply.
 */
export function eventDetailText(
  event: DelayWaitEvent,
  replyChannels: string[] = []
): string {
  if (event.alert_triggered) {
    return alertFilterText(event.alert_triggered.alerts);
  }
  if (event.alert_action_taken) {
    const choice = actionChoiceOf(event.alert_action_taken);
    const action = choice
      ? ACTION_CHOICE_SUMMARY[choice]
      : plural(event.alert_action_taken.action_taken.length, 'action');
    return `${action}, ${alertFilterText(event.alert_action_taken.alerts)}`;
  }
  if (event.status_progress_update) {
    const statuses = event.status_progress_update.statuses;
    return statuses.length === 0
      ? 'no status chosen'
      : statuses.map(statusShortLabel).join(', ');
  }
  if (event.reply_received) {
    if (replyChannels.length === 0) return 'any reply';
    return [...new Set(replyChannels)].join(', ');
  }
  if (event.snooze_ended) return 'Alert no longer snoozed';
  if (event.document_added) return whereSummary(event.document_added.where);
  if (event.note_added) {
    // An omitted value is Anyone. A number this editor does not know is not: the backend matches
    // no note for it.
    const addedBy =
      event.note_added.added_by ?? DelayNoteAddedBy.DELAY_NOTE_ADDED_BY_ANYONE;
    const who =
      NOTE_ADDED_BY_OPTIONS.find((o) => o.value === addedBy)?.by ??
      'an unknown author';
    return `${whereSummary(event.note_added.where)}, added by ${who}`;
  }
  return 'unknown event';
}

/** One line for an event, for example "Alert action taken: Resolved by a person". */
export function eventSummaryText(
  event: DelayWaitEvent,
  replyChannels: string[] = []
): string {
  const kind = getEventKind(event);
  const label = kind ? EVENT_KIND_META[kind].label : 'Unknown event';
  return `${label}: ${eventDetailText(event, replyChannels)}`;
}

/**
 * The node's canvas subtitle, which the canvas shows after "Delay: ". It is always "Until event",
 * as the design says; the event names and the deadline are in the editor, not on the canvas.
 */
export function untilEventSubtitle(untilEvent: DelayUntilEvent): string {
  return untilEvent.events.length === 0 ? '(unconfigured)' : 'Until event';
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/** The message that says why one event cannot be saved, or undefined when it is fine. */
export function eventErrorMessage(event: DelayWaitEvent): string | undefined {
  if (event.alert_triggered) {
    return hasAlertSelection(event.alert_triggered.alerts)
      ? undefined
      : MSG_NEEDS_ALERT;
  }
  if (event.alert_action_taken) {
    const { alerts, select_all_action_taken, action_taken } =
      event.alert_action_taken;
    if (!hasAlertSelection(alerts)) return MSG_NEEDS_ALERT;
    // The editor always sets an action; an empty list can only come from elsewhere.
    return select_all_action_taken || action_taken.length > 0
      ? undefined
      : MSG_NEEDS_ACTION;
  }
  if (event.status_progress_update) {
    const { statuses } = event.status_progress_update;
    if (statuses.length === 0) return MSG_NEEDS_STATUS;
    // A draft saved before the list was narrowed: no checkbox shows it, so name it here.
    const notOffered = statuses.filter((status) => !isOfferedStatus(status));
    return notOffered.length === 0
      ? undefined
      : `Not available yet: ${notOffered.map(statusShortLabel).join(', ')}. Choose from the statuses listed`;
  }
  if (event.document_added || event.note_added) {
    const where = (event.document_added ?? event.note_added)?.where;
    return where === DelayEventWhere.DELAY_EVENT_WHERE_SHIPMENT ||
      where === DelayEventWhere.DELAY_EVENT_WHERE_RUN_ALERT
      ? undefined
      : MSG_NEEDS_WHERE;
  }
  // Reply received may be empty: that means any reply on the shipment. Snooze ended has nothing
  // to choose. Note added always has a "Added by" choice (it starts on Anyone).
  return undefined;
}

/** Errors for a "Delay until event" configuration. */
export function validateUntilEvent(
  untilEvent: DelayUntilEvent,
  graph: GraphData
): ValidationError[] {
  const errors: ValidationError[] = [];

  // Mirrors the publish check: any alert trigger among the workflow's own (not subgraph) nodes.
  const startsFromAlert = graph.nodes.some(
    (node) =>
      !node.parentId &&
      (node.data?.alert_trigger !== undefined ||
        node.data?.alert_action_taken_trigger !== undefined)
  );

  if (untilEvent.events.length === 0) {
    errors.push({
      type: 'REQUIRED_FIELD',
      field: DelayUntilEventFields.EVENTS,
      message: 'Add at least one event',
    });
  }
  for (const event of untilEvent.events) {
    // The events about the alert that started the run, which a run that did not start from one
    // has nothing to look at. The workflow-level problem goes first: choosing alert types would
    // not fix it.
    const alertRunEventName = event.alert_action_taken
      ? 'Alert action taken'
      : event.snooze_ended
        ? 'Snooze ended'
        : undefined;
    const wantsRunAlert =
      (event.document_added ?? event.note_added)?.where ===
      DelayEventWhere.DELAY_EVENT_WHERE_RUN_ALERT;
    const message =
      alertRunEventName && !startsFromAlert
        ? needsAlertTriggerMessage(alertRunEventName)
        : wantsRunAlert && !startsFromAlert
          ? MSG_WHERE_NEEDS_ALERT_TRIGGER
          : eventErrorMessage(event);
    if (message) {
      errors.push({
        type: 'REQUIRED_FIELD',
        field: delayEventField(event.id),
        message,
      });
    }
  }

  const deadline = untilEvent.stop_waiting_after;
  if (!deadline) {
    errors.push({
      type: 'REQUIRED_FIELD',
      field: DelayUntilEventFields.STOP_WAITING_AFTER,
      message: MSG_REQUIRED,
    });
  } else if (!(
    deadline.seconds >= MIN_STOP_WAITING_SECONDS &&
    deadline.seconds <= MAX_STOP_WAITING_SECONDS &&
    // The unit it was typed in must divide it evenly: 1.5 hr is not allowed, 90 min is.
    deadline.seconds % secondsPerUnit(deadline.display_unit) === 0
  )) {
    errors.push({
      type: 'VALUE_ERROR',
      field: DelayUntilEventFields.STOP_WAITING_AFTER,
      message: MSG_DEADLINE_RANGE,
    });
  }

  const notifyOthers = untilEvent.if_nothing_happens?.notify_others;
  if (notifyOthers) {
    if (notifyOthers.channels.length === 0) {
      errors.push({
        type: 'REQUIRED_FIELD',
        field: DelayUntilEventFields.NOTIFY_VIA,
        message: MSG_REQUIRED,
      });
    }
    if (notifyOthers.recipients.length === 0) {
      errors.push({
        type: 'REQUIRED_FIELD',
        field: DelayUntilEventFields.RECIPIENTS,
        message: MSG_REQUIRED,
      });
    }
  }

  return errors;
}
